import type { VenueProfileFieldError } from '@/lib/api/errors'

import { EMPTY_MARK, formatDate } from './format'
import type { VenueImageItem, VenueProfileSaveInput, VenueProfileView } from './venueProfileTypes'

/** 사진 목록의 항목 하나. `status` 로 업로드 진행 상태를 추적한다(설계 §2-1(b)). */
export interface VenueImageFormItem {
  /** 렌더 key 겸 상태 갱신용 식별자 — 서버 값과 무관한 로컬 전용 값이다. */
  clientKey: string
  status: 'done' | 'uploading' | 'error'
  /** 그리드 `<img>` 에 항상 쓰는 값 — `uploading`/`error` 면 로컬 objectURL, `done` 이면 서버 URL. */
  previewUrl: string
  /** `done` 일 때만 값이 있다. 저장 페이로드는 이 필드만 사용한다. */
  imageUrl: string | null
  thumbnailUrl: string | null
  errorMessage?: string
}

let clientKeySeq = 0

/** 사진 항목 로컬 식별자 생성 — 서버 id 가 없는 업로드 중 항목도 추적해야 해서 별도로 둔다. */
export function generateImageClientKey(): string {
  clientKeySeq += 1
  return `venue-image-${Date.now()}-${clientKeySeq}`
}

/**
 * 폼 상태 ↔ `VenueProfileSaveInput` 매핑 + 사전 검증 + 요약 뷰모델 (설계 §2-3, `#867` 설계 §2).
 *
 * 🚨 `if (status === ...)` 를 페이지에 흩지 않는다 — `dashboardViewModel.ts` 가 세운 규칙을 승계한다.
 */

/** 텍스트 입력은 항상 문자열로 다룬다(빈 문자열이 유효한 중간 상태) — 저장 직전에만 `null` 로 정규화한다. */
export interface VenueProfileFormState {
  name: string
  introduction: string
  contact: string
  websiteUrl: string
  address: string
  detailAddress: string
  parkingGuide: string
  /** `#865` 로 3상태 폐기 — 항상 `boolean`. 기본값은 `false`(없음). */
  parkingAvailable: boolean
  /** `#866` 신규 — 주소 검색으로만 함께 채운다. 쌍으로만 유효하다. */
  latitude: number | null
  longitude: number | null
  floorMaterialNotice: string
  showerAvailable: boolean
  lockerRoomAvailable: boolean
  /** `#865` 신규 — 스트링 작업 가능 여부(전용 토글, 편의시설 칩 아님). */
  stringServiceAvailable: boolean
  /** `VenueAmenity.code[]`. */
  amenities: string[]
  rentalEquipments: string[]
  usageGuide: string
  additionalNotice: string
  /** 대표 이미지 단일 슬롯(0 또는 1장). */
  mainImage: VenueImageFormItem | null
  /** 시설 이미지 목록(최대 5장). */
  facilityImages: VenueImageFormItem[]
  /** 이번 편집 세션에서 시설 이미지 목록을 건드렸는가 — `facilityImages` 를 저장 페이로드에 실을지 가른다. */
  facilityImagesDirty: boolean
}

/**
 * `#884` — 그리드 미리보기는 `thumbnailUrl`이 있으면 그것을, 없으면 원본(`imageUrl`)을 쓴다(폴백).
 * 🚨 `imageUrl`/`thumbnailUrl` 필드 자체(저장 페이로드가 쓰는 값)는 그대로 원본을 보존한다 —
 * `previewUrl`만 표시 전용으로 갈린다.
 */
function toDoneImageFormItem(imageUrl: string, thumbnailUrl: string | null): VenueImageFormItem {
  return {
    clientKey: generateImageClientKey(),
    status: 'done',
    previewUrl: thumbnailUrl ?? imageUrl,
    imageUrl,
    thumbnailUrl,
  }
}

/** `#884` — 시설 이미지 표시 순서. 서버가 이미 `displayOrder` 순으로 내려주지만 방어적으로 다시 정렬한다. */
function sortByDisplayOrder(items: VenueImageItem[]): VenueImageItem[] {
  return [...items].sort((a, b) => a.displayOrder - b.displayOrder)
}

/** `GET`/`PUT` 응답 → 폼 초기값. `null` 텍스트 필드는 빈 문자열로 편다. */
export function toFormState(view: VenueProfileView): VenueProfileFormState {
  return {
    name: view.basic.name,
    introduction: view.basic.introduction ?? '',
    contact: view.basic.contact ?? '',
    websiteUrl: view.basic.websiteUrl ?? '',
    address: view.location.address,
    detailAddress: view.location.detailAddress ?? '',
    parkingGuide: view.location.parkingGuide ?? '',
    parkingAvailable: view.location.parkingAvailable,
    latitude: view.location.latitude,
    longitude: view.location.longitude,
    floorMaterialNotice: view.facility.floorMaterialNotice ?? '',
    showerAvailable: view.facility.showerAvailable,
    lockerRoomAvailable: view.facility.lockerRoomAvailable,
    stringServiceAvailable: view.facility.stringServiceAvailable,
    amenities: view.facility.amenities.map((item) => item.code),
    rentalEquipments: [...view.facility.rentalEquipments],
    usageGuide: view.operation.usageGuide ?? '',
    additionalNotice: view.operation.additionalNotice ?? '',
    mainImage: view.image.imageUrl ? toDoneImageFormItem(view.image.imageUrl, view.image.thumbnailUrl) : null,
    facilityImages: sortByDisplayOrder(view.image.facilityImages).map((item) => toDoneImageFormItem(item.imageUrl, item.thumbnailUrl)),
    facilityImagesDirty: false,
  }
}

/** 빈 문자열/공백뿐인 문자열은 미입력(`null`)으로 정규화한다(서버 규칙과 동일 — §2 "공백뿐인 선택 문자열"). */
function toNullableText(value: string): string | null {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** `status:'done'` 인 항목만 저장 대상이다 — 업로드 중·실패 항목은 아직 URL 이 없다. */
function doneImages(images: VenueImageFormItem[]): (VenueImageFormItem & { imageUrl: string })[] {
  return images.filter((item): item is VenueImageFormItem & { imageUrl: string } => item.status === 'done' && Boolean(item.imageUrl))
}

/**
 * 폼 → `PUT` 요청 바디. **전량 교체**이므로 항상 전 필드를 채워 보낸다(#594 D5).
 *
 * 🚨 `facilityImages` 만 예외다(#865 §6-B) — 이번 세션에서 건드리지 않았으면
 * (`facilityImagesDirty === false`) 키 자체를 뺀다("보존"). `imageUrl`/`thumbnailUrl`(대표 1장)·
 * `latitude`/`longitude`는 예외 대상이 아니라 항상 채워 보낸다.
 */
export function toSaveInput(form: VenueProfileFormState): VenueProfileSaveInput {
  const mainImage = form.mainImage && form.mainImage.status === 'done' ? form.mainImage : null
  const savedFacilityImages = doneImages(form.facilityImages)

  return {
    name: form.name.trim(),
    introduction: toNullableText(form.introduction),
    contact: form.contact.trim(),
    websiteUrl: toNullableText(form.websiteUrl),
    address: form.address.trim(),
    detailAddress: toNullableText(form.detailAddress),
    parkingGuide: toNullableText(form.parkingGuide),
    parkingAvailable: form.parkingAvailable,
    latitude: form.latitude,
    longitude: form.longitude,
    floorMaterialNotice: toNullableText(form.floorMaterialNotice),
    showerAvailable: form.showerAvailable,
    lockerRoomAvailable: form.lockerRoomAvailable,
    stringServiceAvailable: form.stringServiceAvailable,
    amenities: form.amenities,
    rentalEquipments: form.rentalEquipments.map((item) => item.trim()).filter((item) => item.length > 0),
    usageGuide: toNullableText(form.usageGuide),
    additionalNotice: toNullableText(form.additionalNotice),
    imageUrl: mainImage?.imageUrl ?? null,
    thumbnailUrl: mainImage?.thumbnailUrl ?? null,
    ...(form.facilityImagesDirty
      ? { facilityImages: savedFacilityImages.map((item) => ({ imageUrl: item.imageUrl, thumbnailUrl: item.thumbnailUrl })) }
      : {}),
  }
}

/** `#594` §2 필드 검증표 — 상한·형식을 그대로 미러링한다(서버가 최종 판정자, 이건 즉시 피드백용). */
export const VENUE_PROFILE_LIMITS = {
  name: 100,
  introduction: 1000,
  contact: 30,
  websiteUrl: 500,
  address: 200,
  detailAddress: 200,
  parkingGuide: 500,
  floorMaterialNotice: 100,
  usageGuide: 2000,
  additionalNotice: 1000,
  amenitiesMax: 12,
  rentalEquipmentsMax: 10,
  rentalEquipmentItemMax: 30,
  /** `#865` §6-B — 시설 이미지 상한(대표는 슬롯 1개라 별도 상수가 필요 없다). */
  facilityImagesMax: 5,
  imageUrlMax: 500,
} as const

const URL_PATTERN = /^https?:\/\/\S+$/

/**
 * 휴대폰 접두(3자리, 최종 11자리 고정) — 이 접두를 만나면 말미 4자리 고정 포맷을 쓴다(`#875` 버그 수정).
 */
const MOBILE_CONTACT_PREFIXES = ['010', '011', '016', '017', '018', '019']

/**
 * 대표 연락처 자동 하이픈 포맷(`#867` 요구사항 3) — 백엔드 `VenueContact.formatOf` 매핑표
 * (02+7~8자리 / 0XX+7~8자리 / 1XXX+4자리)를 그대로 반영한다. 타이핑마다 숫자만 뽑아 다시 조립하므로
 * 커서 위치와 무관하게 항상 같은 결과를 낸다(`formatPhoneInput`, `reservationManualCreateViewModel.ts`
 * 와 같은 방식 — 다만 이 함수는 010 전용이 아니라 지역번호·대표번호까지 다룬다).
 *
 * 🚨 알려진 패턴에 맞지 않는 자릿수는 하이픈 없이 숫자만 반환한다 — 서버와 동일하게 "포맷 실패를
 * 예외로 만들지 않는다"(백엔드 D-C1).
 *
 * 🐛 `#875` 수정 — 휴대폰(최종 11자리)은 지역번호(최종 10자리)와 **다른 임계값**으로 그룹을 나눠야
 * 하는데, 이전 구현은 두 경우를 같은 `0XX` 분기·같은 임계값(6·10)으로 처리해 휴대폰 입력이 7~10자리
 * 구간에서 `010-404-0` 처럼 지역번호 기준(3-3-n)으로 조기 분할됐다가 11자리에서야 `3-4-4`로 재조립됐다
 * (타이핑 도중 그룹이 갈라졌다 합쳐지는 결함). 알려진 휴대폰 접두는 처음부터 **말미 4자리를 고정**하고
 * 그 앞을 채우는 방식으로 포맷해 자릿수가 늘수록 마지막 그룹만 자라 `3-4-4`로 매끄럽게 수렴하게 한다.
 * `02`·기타 지역번호(0XX, 최종 10자리 고정)·`1XXX`(대표번호) 분기는 이 결함이 없어 손대지 않았다.
 */
export function formatVenueContactInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11)

  // 1XXX + 4자리(대표번호, 예: 1588-0000).
  if (/^1\d{3}/.test(digits)) {
    if (digits.length <= 4) return digits
    return `${digits.slice(0, 4)}-${digits.slice(4, 8)}`
  }

  // 02 + 7~8자리(서울 지역번호).
  if (digits.startsWith('02')) {
    if (digits.length <= 2) return digits
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`
  }

  // 010/011/016/017/018/019(휴대폰, 최종 11자리) — 말미 4자리 고정.
  if (MOBILE_CONTACT_PREFIXES.some((prefix) => digits.startsWith(prefix))) {
    const prefixPart = digits.slice(0, 3)
    const rest = digits.slice(3)
    if (rest.length === 0) return prefixPart
    if (rest.length <= 4) return `${prefixPart}-${rest}`
    return `${prefixPart}-${rest.slice(0, 4)}-${rest.slice(4)}`
  }

  // 0XX(3자리 지역번호) + 7~8자리.
  if (digits.startsWith('0')) {
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
    if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`
  }

  // 알려진 접두(0/1)가 아니면 숫자만 반환.
  return digits
}

/**
 * 클라이언트 사전 검증. 서버 검증표(#594 §2)와 상한·형식이 일치해야 한다(T2 가 이 일치를 고정한다).
 * 🚨 모든 위반을 한 번에 수집한다(fail-fast 아님) — 서버 규칙(§2 "모든 위반을 한 번에 돌려준다")과
 * 같은 UX 를 클라이언트에서도 낸다.
 */
export function validateVenueProfileForm(form: VenueProfileFormState): VenueProfileFieldError[] {
  const errors: VenueProfileFieldError[] = []

  function checkText(
    field: keyof typeof VENUE_PROFILE_LIMITS,
    value: string,
    required: boolean,
    pattern?: { regex: RegExp; message: string }
  ) {
    const trimmed = value.trim()
    if (required && trimmed.length === 0) {
      errors.push({ field, code: 'REQUIRED', message: `${FIELD_LABELS[field]}을(를) 입력해 주세요.` })
      return
    }
    if (trimmed.length === 0) return
    const max = VENUE_PROFILE_LIMITS[field] as number
    if (trimmed.length > max) {
      errors.push({
        field,
        code: 'TOO_LONG',
        message: `${FIELD_LABELS[field]}은(는) 최대 ${max}자까지 입력할 수 있습니다.`,
      })
      return
    }
    if (pattern && !pattern.regex.test(trimmed)) {
      errors.push({ field, code: 'INVALID_FORMAT', message: pattern.message })
    }
  }

  checkText('name', form.name, true)
  // 🚨 연락처는 자동 포맷 결과(하이픈 포함)를 그대로 검증한다 — `formatVenueContactInput` 이 항상
  // 알려진 패턴 아니면 숫자만 남기므로 이 정규식과 어긋나지 않는다.
  checkText('contact', form.contact, true, {
    regex: /^(0\d{1,2}-?\d{3,4}-?\d{4}|1\d{3}-?\d{4})$/,
    message: '대표 연락처 형식이 올바르지 않습니다. (예: 02-000-0000)',
  })
  checkText('websiteUrl', form.websiteUrl, false, {
    regex: URL_PATTERN,
    message: '웹사이트 URL 형식이 올바르지 않습니다. (http:// 또는 https:// 로 시작)',
  })
  checkText('address', form.address, true)
  checkText('detailAddress', form.detailAddress, false)
  checkText('parkingGuide', form.parkingGuide, false)
  checkText('floorMaterialNotice', form.floorMaterialNotice, false)
  checkText('introduction', form.introduction, false)
  checkText('usageGuide', form.usageGuide, false)
  checkText('additionalNotice', form.additionalNotice, false)

  if (form.amenities.length > VENUE_PROFILE_LIMITS.amenitiesMax) {
    errors.push({
      field: 'amenities',
      code: 'TOO_MANY_ITEMS',
      message: `편의시설은 최대 ${VENUE_PROFILE_LIMITS.amenitiesMax}개까지 선택할 수 있습니다.`,
    })
  }

  if (form.rentalEquipments.length > VENUE_PROFILE_LIMITS.rentalEquipmentsMax) {
    errors.push({
      field: 'rentalEquipments',
      code: 'TOO_MANY_ITEMS',
      message: `대여 장비는 최대 ${VENUE_PROFILE_LIMITS.rentalEquipmentsMax}개까지 등록할 수 있습니다.`,
    })
  } else if (form.rentalEquipments.some((item) => item.trim().length === 0)) {
    errors.push({ field: 'rentalEquipments', code: 'BLANK_ITEM', message: '빈 대여 장비 항목이 있습니다.' })
  } else if (form.rentalEquipments.some((item) => item.trim().length > VENUE_PROFILE_LIMITS.rentalEquipmentItemMax)) {
    errors.push({
      field: 'rentalEquipments',
      code: 'TOO_LONG',
      message: `대여 장비 이름은 항목당 최대 ${VENUE_PROFILE_LIMITS.rentalEquipmentItemMax}자까지 입력할 수 있습니다.`,
    })
  }

  // 🚨 이번 세션에서 건드리지 않았으면(facilityImagesDirty=false) 검사하지 않는다 — 과거에 저장된,
  // 이번 편집과 무관한 사진 데이터 때문에 "이름만 고치려던 저장"까지 막히면 안 된다.
  if (form.facilityImagesDirty) {
    const saved = form.facilityImages.filter((item) => item.status === 'done')
    if (saved.length > VENUE_PROFILE_LIMITS.facilityImagesMax) {
      errors.push({
        field: 'facilityImages',
        code: 'TOO_MANY_ITEMS',
        message: `시설 이미지는 최대 ${VENUE_PROFILE_LIMITS.facilityImagesMax}장까지 등록할 수 있습니다. (입력=${saved.length}장)`,
      })
    } else if (saved.some((item) => !item.imageUrl || item.imageUrl.trim().length === 0)) {
      errors.push({ field: 'facilityImages', code: 'BLANK_ITEM', message: '빈 사진이 있습니다.' })
    } else if (
      saved.some(
        (item) => !URL_PATTERN.test(item.imageUrl as string) || (item.imageUrl as string).length > VENUE_PROFILE_LIMITS.imageUrlMax
      )
    ) {
      errors.push({ field: 'facilityImages', code: 'INVALID_FORMAT', message: '사진 URL 형식이 올바르지 않습니다.' })
    }
  }

  return errors
}

const FIELD_LABELS: Record<string, string> = {
  name: '구장명',
  introduction: '구장 소개',
  contact: '대표 연락처',
  websiteUrl: '웹사이트 URL',
  address: '주소',
  detailAddress: '상세 주소',
  parkingGuide: '주차 안내',
  floorMaterialNotice: '코트 바닥 재질',
  usageGuide: '이용 안내',
  additionalNotice: '추가 안내',
}

/** `field` → 메시지. 서버·클라이언트 검증 결과 둘 다 이 형태로 화면에 바인딩한다. */
export function toFieldErrorMap(errors: VenueProfileFieldError[] | undefined): Record<string, string> {
  if (!errors) return {}
  return Object.fromEntries(errors.map((error) => [error.field, error.message]))
}

/**
 * 사진 목록 순수 배열 변환 — `VenueFacilityImageListEditor` 가 이 함수들만 호출하고 배열을 직접
 * 조작하지 않는다(`if (status === ...)` 를 컴포넌트에 흩지 않는다는 파일 상단 규칙의 연장).
 */

/** 파일 선택 직후 — 업로드 중 상태로 항목을 추가한다. */
export function addUploadingImage(images: VenueImageFormItem[], clientKey: string, previewUrl: string): VenueImageFormItem[] {
  return [...images, { clientKey, status: 'uploading', previewUrl, imageUrl: null, thumbnailUrl: null }]
}

/** 업로드 성공 — 해당 항목을 완료 상태로 바꾸고 서버 URL 로 미리보기를 교체한다. */
export function resolveImageUpload(
  images: VenueImageFormItem[],
  clientKey: string,
  result: { imageUrl: string; thumbnailUrl: string | null }
): VenueImageFormItem[] {
  return images.map((item) =>
    item.clientKey === clientKey
      ? { ...item, status: 'done', previewUrl: result.imageUrl, imageUrl: result.imageUrl, thumbnailUrl: result.thumbnailUrl }
      : item
  )
}

/** 업로드 실패 — 재시도/삭제할 수 있도록 오류 상태로 남긴다. */
export function failImageUpload(images: VenueImageFormItem[], clientKey: string, message: string): VenueImageFormItem[] {
  return images.map((item) => (item.clientKey === clientKey ? { ...item, status: 'error' as const, errorMessage: message } : item))
}

/** 목록에서 제거 — Storage 의 실제 blob 은 지우지 않는다(설계 §2 자율 결정). */
export function removeImage(images: VenueImageFormItem[], clientKey: string): VenueImageFormItem[] {
  return images.filter((item) => item.clientKey !== clientKey)
}

/** 위/아래 이동. 경계에서는 아무 것도 하지 않는다. */
export function moveImage(images: VenueImageFormItem[], clientKey: string, direction: 'up' | 'down'): VenueImageFormItem[] {
  const index = images.findIndex((item) => item.clientKey === clientKey)
  if (index === -1) return images
  const targetIndex = direction === 'up' ? index - 1 : index + 1
  if (targetIndex < 0 || targetIndex >= images.length) return images

  const next = [...images]
  const [moved] = next.splice(index, 1)
  next.splice(targetIndex, 0, moved as VenueImageFormItem)
  return next
}

/**
 * 대표 이미지 단일 슬롯 순수 변환 — `VenueMainImageField` 가 이 함수들만 호출한다. 배열 기반
 * 헬퍼(`addUploadingImage` 등)와 달리 슬롯이 하나뿐이라 항상 기존 값을 덮어쓴다(선택 = 교체).
 */

export function uploadingMainImage(clientKey: string, previewUrl: string): VenueImageFormItem {
  return { clientKey, status: 'uploading', previewUrl, imageUrl: null, thumbnailUrl: null }
}

export function resolvedMainImage(
  clientKey: string,
  result: { imageUrl: string; thumbnailUrl: string | null }
): VenueImageFormItem {
  return { clientKey, status: 'done', previewUrl: result.imageUrl, imageUrl: result.imageUrl, thumbnailUrl: result.thumbnailUrl }
}

export function failedMainImage(clientKey: string, previewUrl: string, message: string): VenueImageFormItem {
  return { clientKey, status: 'error', previewUrl, imageUrl: null, thumbnailUrl: null, errorMessage: message }
}

/**
 * 주소를 수동으로 다시 입력하면 좌표를 함께 지운다(`#867` 설계 §2 자율 결정 8) — 검색으로 얻은 좌표를
 * 다른 주소에 그대로 남기지 않기 위함이다. 주소 검색 버튼으로 채운 직후에는 이 함수를 거치지 않는다
 * (페이지가 `address`/`latitude`/`longitude` 를 한 번에 갱신한다).
 */
export function clearCoordinatesOnAddressEdit(form: VenueProfileFormState, address: string): VenueProfileFormState {
  return { ...form, address, latitude: null, longitude: null }
}

/** 요약 화면에서 아직 원천이 없는 값의 표시 문구(§2-1(c)) — 실제 값을 추측·하드코딩하지 않는다. */
export const NOT_AVAILABLE_TEXT = '준비 중'

export interface VenueProfileSummaryViewModel {
  name: string
  address: string
  courtCountText: string
  operatingHoursText: string
  reservationUnitText: string
  maxReservationDurationText: string
  nextClosureDateText: string
  blockedTimeText: string
}

/**
 * 요약 화면 뷰모델. 구장명·주소·코트 수는 `#594` 실데이터, 나머지 5항목은 원천 도메인이 아직 없어
 * "준비 중"으로 고정한다(설계 §2-1(c) D3 — 값을 추측하지 않는다).
 */
export function toVenueProfileSummaryViewModel(view: VenueProfileView): VenueProfileSummaryViewModel {
  return {
    name: view.basic.name,
    address: view.location.address,
    courtCountText: `${view.summary.courtCount}면`,
    operatingHoursText: NOT_AVAILABLE_TEXT,
    reservationUnitText: NOT_AVAILABLE_TEXT,
    maxReservationDurationText: NOT_AVAILABLE_TEXT,
    nextClosureDateText: NOT_AVAILABLE_TEXT,
    blockedTimeText: NOT_AVAILABLE_TEXT,
  }
}

export interface VenueProfileOverviewViewModel {
  name: string
  address: string
  contact: string
  websiteUrlText: string
  courtCountText: string
  lastModifiedAtText: string
}

/** 편집 화면 우측 "현재 구장 정보 요약" 패널(요구사항 명시 6필드). */
export function toVenueProfileOverviewViewModel(view: VenueProfileView): VenueProfileOverviewViewModel {
  return {
    name: view.basic.name,
    address: view.location.address,
    contact: view.basic.contact ?? EMPTY_MARK,
    websiteUrlText: view.basic.websiteUrl ?? EMPTY_MARK,
    courtCountText: `${view.summary.courtCount}면`,
    lastModifiedAtText: formatDate(view.summary.lastModifiedAt),
  }
}
